// ============================================================================
// STOCK CARD — Inventory slice 2 (ADR-0058 §10).
// One item's stock movements for a period with a running quantity and value:
// opening balance, every movement (in / out, unit cost, signed value, cost
// basis), closing balance. Optional location; empty = all locations. Not
// server-paged — the date range bounds it.
// Opened directly, or from Stock Position via the navigation seam:
//   openRecord('stock-card', { recordId: itemId, label: 'CODE — Name',
//     intent: 'stockLocationId=…&locationLabel=…&toDate=yyyy-MM-dd' })
// Drill: an Opening Stock movement's reference opens that Opening Stock document.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption } from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useNavigation, useNavTarget } from 'contexts/NavigationContext';
import { useStockCard } from 'hooks/api/useInventory';
import { itemService, stockLocationService } from 'api/services';
import {
  MOVEMENT_TYPE_LABELS,
  type StockCardLineDto, type StockCardQueryParams,
} from 'api/types/inventory.types';
import ReportHeader from 'components/reports/ReportHeader';
import DrillableCell from 'components/reports/DrillableCell';

const ListIcon = IconSystem.list;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const isoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => isoDate(new Date());
const monthStartOf = (yyyyMmDd: string) => `${yyyyMmDd.slice(0, 7)}-01`;
const fmtDate = (s?: string | null) => (s ? new Date(`${s.slice(0, 10)}T00:00:00`).toLocaleDateString() : '');
const fmtMoney = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 6 });

const COST_BASIS_LABELS: Record<StockCardLineDto['costSource'], string> = {
  ENTERED: 'Entered',
  AVERAGE: 'Average',
  LINKED: 'Original cost',
};

const inp: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };
const right: React.CSSProperties = { display: 'block', textAlign: 'right' };
const muted: React.CSSProperties = { color: 'var(--text-muted)' };
const balanceRowLabel: React.CSSProperties = { fontWeight: 700, fontStyle: 'italic', color: 'var(--text-secondary)' };

/** Grid row: a movement, or the synthetic opening / closing balance rows. */
interface CardRow {
  id: string;
  kind: 'OPENING' | 'LINE' | 'CLOSING';
  line?: StockCardLineDto;
  balanceQty: number;
  balanceValue: number;
}

interface AppliedFilters {
  itemId: string;
  stockLocationId: string;
  fromDate: string;
  toDate: string;
}

interface StockCardScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const StockCardScreen: React.FC<StockCardScreenProps> = ({ tabId }) => {
  const { openRecord } = useNavigation();

  // ---- Applied filters (drive the fetch) ----
  const [applied, setApplied] = useState<AppliedFilters>({
    itemId: '', stockLocationId: '', fromDate: monthStartOf(today()), toDate: today(),
  });
  const params: StockCardQueryParams | undefined = useMemo(() => (applied.itemId ? {
    itemId: applied.itemId,
    stockLocationId: applied.stockLocationId || undefined,
    fromDate: applied.fromDate,
    toDate: applied.toDate,
  } : undefined), [applied]);
  const { data, loading, error, refetch } = useStockCard(params);
  useEffect(() => {
    if (error) {
      const e = error as { response?: { data?: { message?: string } } };
      notificationService.toast.error(e?.response?.data?.message || 'Could not load the stock card.');
    }
  }, [error]);

  // ---- Draft filters (filter panel; open until an item is chosen) ----
  const [filterOpen, setFilterOpen] = useState(true);
  const [fItemId, setFItemId] = useState('');
  const [fLocationId, setFLocationId] = useState('');
  const [fFromDate, setFFromDate] = useState(applied.fromDate);
  const [fToDate, setFToDate] = useState(applied.toDate);
  const itemLabels = useRef<Map<string, string>>(new Map());
  const locationLabels = useRef<Map<string, string>>(new Map());

  const loadItems = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await itemService.getAll({ searchTerm: input || undefined, pageSize: 30, itemType: 'STOCK' });
      return res.data.map(i => {
        const label = `${i.itemCode} — ${i.itemName}`;
        itemLabels.current.set(i.id, label);
        return { value: i.id, label };
      });
    } catch {
      return [];
    }
  }, []);

  const loadLocations = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await stockLocationService.lookup({ searchTerm: input || undefined, pageSize: 50 });
      return res.data.map(l => {
        const label = `${l.locationCode} — ${l.locationName}`;
        locationLabels.current.set(l.id, label);
        return { value: l.id, label };
      });
    } catch {
      return [];
    }
  }, []);

  // ---- Arrival from Stock Position: item (+ location) up to its as-at date ----
  useNavTarget('stock-card', (t) => {
    const q = new URLSearchParams(t.intent || '');
    const locId = q.get('stockLocationId') || '';
    const to = q.get('toDate') || today();
    const from = monthStartOf(to);
    if (t.label) itemLabels.current.set(t.recordId, t.label);
    const locLabel = q.get('locationLabel');
    if (locId && locLabel) locationLabels.current.set(locId, locLabel);
    setFItemId(t.recordId); setFLocationId(locId); setFFromDate(from); setFToDate(to);
    setApplied({ itemId: t.recordId, stockLocationId: locId, fromDate: from, toDate: to });
    setFilterOpen(false);
  });

  const applyFilters = useCallback(() => {
    if (!fItemId) { notificationService.toast.warning('Choose an item.'); return; }
    if (!fFromDate || !fToDate) { notificationService.toast.warning('Period (From / To) is required.'); return; }
    if (fFromDate > fToDate) { notificationService.toast.warning('From date must be on or before To date.'); return; }
    setApplied({ itemId: fItemId, stockLocationId: fLocationId, fromDate: fFromDate, toDate: fToDate });
    setFilterOpen(false);
  }, [fItemId, fLocationId, fFromDate, fToDate]);

  const clearFilters = useCallback(() => {
    const t = today();
    setFItemId(''); setFLocationId(''); setFFromDate(monthStartOf(t)); setFToDate(t);
    setApplied({ itemId: '', stockLocationId: '', fromDate: monthStartOf(t), toDate: t });
  }, []);

  const appliedCount = applied.itemId ? 1 + (applied.stockLocationId ? 1 : 0) : 0;

  // ---- Grid rows: opening, movements, closing ----
  const rows: CardRow[] = useMemo(() => {
    if (!data) return [];
    return [
      { id: 'opening', kind: 'OPENING', balanceQty: data.openingQuantity, balanceValue: data.openingValue },
      ...data.lines.map((l, i): CardRow => ({
        id: `${l.movementId}-${i}`, kind: 'LINE', line: l, balanceQty: l.runningQuantity, balanceValue: l.runningValue,
      })),
      { id: 'closing', kind: 'CLOSING', balanceQty: data.closingQuantity, balanceValue: data.closingValue },
    ];
  }, [data]);

  const openDocument = useCallback((l: StockCardLineDto) => {
    openRecord('opening-stock', { recordId: l.sourceDocumentId, mode: 'view', label: l.sourceReference || undefined });
  }, [openRecord]);

  const columns: SyutGridColumn<CardRow>[] = useMemo(() => [
    {
      id: 'date', header: 'Date', size: 100,
      cell: ({ row }) => {
        const r = row.original;
        if (r.kind === 'OPENING') return <span style={balanceRowLabel}>Opening balance</span>;
        if (r.kind === 'CLOSING') return <span style={balanceRowLabel}>Closing balance</span>;
        return fmtDate(r.line?.movementDate);
      },
    },
    {
      id: 'type', header: 'Type', size: 140,
      cell: ({ row }) => {
        const l = row.original.line;
        return l ? (MOVEMENT_TYPE_LABELS[l.movementType] || l.movementType) : null;
      },
    },
    {
      id: 'document', header: 'Document', size: 150,
      cell: ({ row }) => {
        const l = row.original.line;
        if (!l) return null;
        const ref = l.sourceReference || '—';
        if (l.sourceModule === 'OPENING_STOCK' && l.sourceDocumentId) {
          return <DrillableCell onClick={() => openDocument(l)} title="Open the opening stock document">{ref}</DrillableCell>;
        }
        return <span title={l.narration || undefined}>{ref}</span>;
      },
    },
    { id: 'location', header: 'Location', size: 110, cell: ({ row }) => row.original.line?.locationCode || null },
    {
      id: 'in', header: 'In', align: 'right', size: 100,
      cell: ({ row }) => {
        const l = row.original.line;
        return l && l.inQuantity ? <span style={right}>{fmtQty(l.inQuantity)}</span> : null;
      },
    },
    {
      id: 'out', header: 'Out', align: 'right', size: 100,
      cell: ({ row }) => {
        const l = row.original.line;
        return l && l.outQuantity ? <span style={right}>{fmtQty(l.outQuantity)}</span> : null;
      },
    },
    {
      id: 'unitCost', header: 'Unit cost', align: 'right', size: 110,
      cell: ({ row }) => {
        const l = row.original.line;
        return l ? <span style={right}>{fmtMoney(l.unitCost)}</span> : null;
      },
    },
    {
      id: 'value', header: 'Value', align: 'right', size: 120,
      cell: ({ row }) => {
        const l = row.original.line;
        if (!l) return null;
        return (
          <span style={{ ...right, color: l.value < 0 ? 'var(--danger-color, #dc2626)' : undefined }}>{fmtMoney(l.value)}</span>
        );
      },
    },
    {
      id: 'costBasis', header: 'Cost basis', size: 110,
      cell: ({ row }) => {
        const l = row.original.line;
        return l ? <span style={muted}>{COST_BASIS_LABELS[l.costSource] || l.costSource}</span> : null;
      },
    },
    {
      id: 'balanceQty', header: 'Balance qty', align: 'right', size: 120,
      cell: ({ row }) => (
        <span style={{ ...right, fontWeight: row.original.kind === 'LINE' ? undefined : 700 }}>{fmtQty(row.original.balanceQty)}</span>
      ),
    },
    {
      id: 'balanceValue', header: 'Balance value', align: 'right', size: 130,
      cell: ({ row }) => (
        <span style={{
          ...right, fontWeight: row.original.kind === 'LINE' ? undefined : 700,
          color: row.original.balanceValue < 0 ? 'var(--danger-color, #dc2626)' : undefined,
        }}>
          {fmtMoney(row.original.balanceValue)}
        </span>
      ),
    },
  ], [openDocument]);

  const actionButtons: ActionButton[] = [actionButtonFactory.refresh(refetch)];

  const itemLabel = data
    ? `${data.itemCode} — ${data.itemName}`
    : (applied.itemId ? itemLabels.current.get(applied.itemId) || '' : '');
  const locationLabel = data?.locationName
    || (applied.stockLocationId ? locationLabels.current.get(applied.stockLocationId) || '' : 'All locations');

  return (
    <EnterpriseScreenShell
      title="Stock Card"
      description="One item's movements with a running quantity and value"
      icon={<ListIcon size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Item" required size="sm" isAsync loadOptions={loadItems} defaultOptions cacheOptions={false}
                  seedOptions={fItemId ? [{ value: fItemId, label: itemLabels.current.get(fItemId) || '' }] : undefined}
                  value={fItemId} onChange={v => setFItemId((v as string) || '')}
                  placeholder="Choose a stock item" isClearable menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Location" size="sm" isAsync loadOptions={loadLocations} defaultOptions cacheOptions={false}
                  seedOptions={fLocationId ? [{ value: fLocationId, label: locationLabels.current.get(fLocationId) || '' }] : undefined}
                  value={fLocationId} onChange={v => setFLocationId((v as string) || '')}
                  placeholder="All locations" isClearable menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                  From <span className="text-danger">*</span>
                </label>
                <input type="date" className="form-input" value={fFromDate} onChange={e => setFFromDate(e.target.value)} style={inp} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                  To <span className="text-danger">*</span>
                </label>
                <input type="date" className="form-input" value={fToDate} onChange={e => setFToDate(e.target.value)} style={inp} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters} disabled={loading}>
                {loading ? 'Loading…' : 'Apply'}
              </button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader
        filters={[
          { label: 'Item', value: itemLabel || '—' },
          { label: 'Base unit', value: data?.baseUomCode || '—' },
          { label: 'Location', value: locationLabel },
          {
            label: 'Period',
            value: `${fmtDate(data?.fromDate || applied.fromDate)} — ${fmtDate(data?.toDate || applied.toDate)}`,
          },
        ]}
      />
      {!applied.itemId && (
        <div style={{
          color: 'var(--text-muted)', padding: '2rem', textAlign: 'center',
          border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 0.5rem 0.5rem',
        }}>
          Choose an item to see its stock card.
        </div>
      )}
      {applied.itemId && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
          border: '1px solid var(--border-color)', borderTop: 'none',
          borderRadius: '0 0 0.5rem 0.5rem', overflow: 'hidden',
        }}>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <SyutGrid<CardRow>
              data={rows}
              columns={columns}
              enableSorting={false}
              isLoading={loading}
              emptyMessage={loading ? 'Loading...' : 'No stock card for this item.'}
            />
          </div>
          {data && data.lines.length === 0 && (
            <div style={{
              padding: '0.5rem 0.75rem', borderTop: '1px solid var(--border-color)',
              fontSize: '0.8125rem', color: 'var(--text-muted)',
            }}>
              No movements in this period.
            </div>
          )}
        </div>
      )}
    </EnterpriseScreenShell>
  );
};

export default StockCardScreen;
